import { motion } from "framer-motion";
import clsx from "clsx";
import { useMemo, useState } from "react";
import Marquee from "react-fast-marquee";
import { useNavigate } from "react-router-dom";
import ClientProviderInfoModal from "src/components/ClientProviderInfoModal/ClientProviderInfoModal";
import Icon from "src/components/Icon/Icon";
import {
  ETransactionType,
  ICON_SIZES,
  SOURCES_DEFAULT_ICON,
} from "src/helpers/constants";
import { rgbaObjectToString } from "src/helpers/utils";
import useAllTransactions from "src/hooks/useAllTransactions";
import useDarkMode from "src/hooks/useDarkMode";
import useMobile from "src/hooks/useMobile";
import { useTranslation } from "react-i18next";

interface ITransactionClientProviderInfo {
  clientProvider: string;
  transactionType: ETransactionType;
  onNonExistingClick?: () => void;
}

const MAX_CHARACTERS_TO_CHANGE_LAYOUT = 200;
const MAX_CHARACTERS_TO_HIDE_BUTTONS_TEXT = 400;

const TransactionClientProviderInfo: React.FC<
  ITransactionClientProviderInfo
> = ({ clientProvider, transactionType, onNonExistingClick }) => {
  const { t } = useTranslation();
  const { isMobile } = useMobile();
  const navigate = useNavigate();
  const { getSourceCustomSettings, sourceExists, isLoadingCustomizedSources } =
    useAllTransactions();
  const { isEnabled: isDarkMode } = useDarkMode();
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);

  const { icon, picture, iconStyles } = useMemo(() => {
    const res = getSourceCustomSettings(clientProvider);

    return {
      icon: res?.icon || "MdStorefront",
      picture: res?.picture || null,
      iconStyles: {
        background: rgbaObjectToString(res?.bg),
        color: rgbaObjectToString(res?.color),
      },
    };
  }, [
    getSourceCustomSettings,
    clientProvider,
    isLoadingCustomizedSources,
    sourceExists,
    transactionType,
  ]);

  const isExistingSource = useMemo(() => {
    return sourceExists(clientProvider);
  }, [
    clientProvider,
    transactionType,
    isLoadingCustomizedSources,
    sourceExists,
    getSourceCustomSettings,
  ]);

  const selectMessage = useMemo(() => {
    if (transactionType === ETransactionType.INCOME) {
      return t("transaction_page.marquee.income");
    }

    return t("transaction_page.marquee.expense");
  }, [transactionType, t]);

  return (
    <>
      <div
        className={clsx([
          "flex",
          "flex-col",
          "md:flex-row",
          "w-full",
          {
            "cursor-pointer": isExistingSource,
          },
        ])}
        onClick={() => {
          if (isExistingSource) {
            setShowInfoModal(true);
          } else {
            onNonExistingClick?.();
          }
        }}
      >
        <div
          className={clsx([
            "flex",
            "w-full",
            "flex-row",
            "md:flex-row",
            "gap-3",
            "md:gap-2",
            "items-center",
            "bg-blue-50/80",
            "dark:bg-gray-900/80",
            "rounded-md",
            "md:rounded-lg",
            "md:mt-2",
          ])}
        >
          <div
            className={clsx([
              "flex",
              "w-full",
              "md:flex-row",
              "gap-0",
              "items-center",
              "flex-row",
            ])}
            key={`${clientProvider}-2`}
          >
            {isExistingSource ? (
              <>
                {picture?.thumbnailUrl ? (
                  <motion.img
                    className={clsx([
                      "flex",
                      `!min-h-[48px]`,
                      `!min-w-[48px]`,
                      `!max-h-[48px]`,
                      `!max-w-[48px]`,
                      `md:!min-h-[64px]`,
                      `md:!min-w-[64px]`,
                      `md:!max-h-[64px]`,
                      `md:!max-w-[64px]`,
                      "rounded-md",
                      "rounded-tr-none",
                      "rounded-br-none",
                      "bg-checkers",
                    ])}
                    src={picture?.thumbnailUrl}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 2, ease: "easeOut" }}
                    key={`${clientProvider}-1`}
                  />
                ) : (
                  <motion.div
                    style={iconStyles}
                    className={clsx([
                      "rounded-md",
                      `!min-h-[48px]`,
                      `!min-w-[48px]`,
                      `!max-h-[48px]`,
                      `!max-w-[48px]`,
                      `md:!min-h-[64px]`,
                      `md:!min-w-[64px]`,
                      `md:!max-h-[64px]`,
                      `md:!max-w-[64px]`,
                      "flex",
                      "items-center",
                      "justify-center",
                      "rounded-tr-none",
                      "rounded-br-none",
                    ])}
                    onClick={() => setShowInfoModal(true)}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 2, ease: "easeOut" }}
                    key={`${clientProvider}-3`}
                  >
                    <Icon
                      name={icon}
                      size={isMobile ? ICON_SIZES.LG : ICON_SIZES.XL}
                      className="shrink-0 p-2 md:p-2"
                    />
                  </motion.div>
                )}
              </>
            ) : (
              <motion.div
                style={iconStyles}
                className={clsx([
                  "rounded-md",
                  `!min-h-[48px]`,
                  `!min-w-[48px]`,
                  `!max-h-[48px]`,
                  `!max-w-[48px]`,
                  `md:!min-h-[64px]`,
                  `md:!min-w-[64px]`,
                  `md:!max-h-[64px]`,
                  `md:!max-w-[64px]`,
                  "flex",
                  "items-center",
                  "justify-center",
                  "rounded-tr-none",
                  "rounded-br-none",
                ])}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 2, ease: "easeOut" }}
                key={`${clientProvider}-4`}
              >
                <Icon
                  name={SOURCES_DEFAULT_ICON}
                  size={isMobile ? ICON_SIZES.LG : ICON_SIZES.XL}
                  className="shrink-0 p-2 md:p-2"
                />
              </motion.div>
            )}

            <motion.div
              className={clsx([
                "flex",
                "overflow-hidden",
                "flex-col",
                "gap-0",
                "w-full",
                "text-sm",
                "md:text-sm",
                "dark:text-cyan-400",
                "text-left",
              ])}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2, ease: "easeOut" }}
              key={clientProvider}
            >
              <Marquee
                autoFill
                gradient={false}
                gradientColor={
                  isDarkMode ? "rgb(17 24 39 / 1)" : "rgb(243 244 246 / 1)"
                }
                gradientWidth={isMobile ? 40 : 60}
                direction={isMobile ? "right" : "left"}
              >
                <div
                  className={clsx([
                    "mx-3",
                    "md:mx-5",
                    "text-sm",
                    "text-blue-900",
                    "dark:text-cyan-400",
                    "md:text-sm",
                    "font-medium",
                  ])}
                >
                  {isExistingSource
                    ? clientProvider
                    : clientProvider?.trim?.()
                    ? clientProvider?.trim?.()
                    : selectMessage}
                </div>
              </Marquee>
            </motion.div>
          </div>
        </div>
      </div>
      {showInfoModal && (
        <ClientProviderInfoModal
          clientProvider={clientProvider}
          onClose={() => setShowInfoModal(false)}
        />
      )}
    </>
  );
};

export default TransactionClientProviderInfo;
